import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BadgeComponent,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  RowComponent,
  SpinnerModule,
  TableModule
} from '@coreui/angular-pro';
import {
  cilArrowBottom,
  cilArrowRight,
  cilArrowTop,
  cilHome,
  cilSitemap,
  cilUser
} from '@coreui/icons';
import { IconDirective, IconSetService } from '@coreui/icons-angular';
import { TranslatePipe, TranslateService } from 'i18n';
import { Logger, UserTreeNode, UserTreeResponse } from 'shared';
import { UserTreeService } from '../../../services/user-tree.service';

@Component({
  selector: 'app-user-tree-page',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ContainerComponent,
    RowComponent,
    ColComponent,
    SpinnerModule,
    TableModule,
    BadgeComponent,
    IconDirective,
    TranslatePipe
  ],
  templateUrl: './user-tree-page.component.html',
  styleUrls: ['./user-tree-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserTreePageComponent {
  private userTreeService = inject(UserTreeService);
  private iconSet = inject(IconSetService);
  private translateService = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  protected logger = new Logger('UserTreePageComponent');

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  treeData = signal<UserTreeResponse | null>(null);
  loading = signal<boolean>(false);
  error = signal<string | null>(null);

  currentUserId = signal<number | 'me'>('me');
  currentPage = signal<number>(1);

  // Sorting (server-side)
  sortColumn = signal<string>('user_id');
  sortDirection = signal<'asc' | 'desc'>('asc');

  // Expose Math for template
  readonly Math = Math;

  // Computed
  pageNumbers = computed<number[]>(() => {
    const data = this.treeData();
    if (!data) return [];

    const total = data.pagination.total_pages;
    const current = data.pagination.current_page;
    const range = 2;
    const pages: number[] = [];
    for (let i = Math.max(1, current - range); i <= Math.min(total, current + range); i++) {
      pages.push(i);
    }
    return pages;
  });

  breadcrumb = computed(() => this.treeData()?.breadcrumb ?? []);

  /** Roll-up of what the viewed node's first line pays. Null on the root listing. */
  summary = computed(() => this.treeData()?.summary ?? null);

  /**
   * Whether this level still carries names/e-mail/phone. Read off the response
   * rather than compared against a hardcoded depth, so the backend stays the single
   * source of truth for how deep personal details reach.
   */
  detailsVisible = computed<boolean>(() => {
    const data = this.treeData();
    return !!data && data.depth <= data.details_visible_depth;
  });

  // ────────────────────────────────────────────────
  // Effects + Initialization
  // ────────────────────────────────────────────────
  constructor() {
    this.iconSet.icons = {
      ...this.iconSet.icons,
      cilArrowRight,
      cilArrowTop,
      cilArrowBottom,
      cilHome,
      cilSitemap,
      cilUser
    };

    // React to route params (userId)
    // effect(() => {
    //   const userIdParam = this.route.snapshot.paramMap.get('userId');
    //   const userId = userIdParam ? parseInt(userIdParam, 10) : 'me';

    //   if (userId !== this.currentUserId()) {
    //     this.currentUserId.set(userId);
    //     this.currentPage.set(1);
    //   }
    // });

    // Sort lives in the URL, not only in these signals: '/user-tree' and
    // '/user-tree/:userId' are separate route configs, so the FIRST drill-down
    // destroys and recreates this component and any signal-only sort is lost.
    // Going through the URL also survives a reload, the back button and a shared
    // link, and keeps one source of truth instead of two that can drift.
    this.route.queryParamMap
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(params => {
          const column = params.get('sort');
          const direction = params.get('dir');
          let changed = false;

          if (column && column !== this.sortColumn()) {
            this.sortColumn.set(column);
            changed = true;
          }
          if ((direction === 'asc' || direction === 'desc') && direction !== this.sortDirection()) {
            this.sortDirection.set(direction);
            changed = true;
          }
          // Reset the page HERE rather than in sortBy(): all three signals then
          // change inside one callback, so the reload effect coalesces into a
          // single request instead of firing once for the page and again for the
          // sort. A re-sorted list makes the old page number meaningless anyway.
          if (changed && this.currentPage() !== 1) {
            this.currentPage.set(1);
          }
        });

    this.route.paramMap
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(params => {
          const userIdParam = params.get('userId');
          const userId = userIdParam ? parseInt(userIdParam, 10) : 'me';

          if (userId !== this.currentUserId()) {
            this.currentUserId.set(userId);
            this.currentPage.set(1);
            // Optional: force reload immediately if you don't want to wait for other effects
            // this.loadTree();
          }
        });

    // Auto-load tree when userId/page/sort changes
    effect(() => {
      this.currentUserId();
      this.currentPage();
      this.sortColumn();
      this.sortDirection();
      this.loadTree();
    });
  }

  // ────────────────────────────────────────────────
  // Core actions
  // ────────────────────────────────────────────────
  private loadTree(): void {
    this.loading.set(true);
    this.error.set(null);

    this.userTreeService.getUserTree(this.currentUserId(), {
      page: this.currentPage(),
      per_page: 50,
      sort_column: this.sortColumn(),
      sort_direction: this.sortDirection()
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: response => {
        this.treeData.set(response);
        this.loading.set(false);
        this.logger.debug('Tree loaded:', response.sponsored_users.length);
      },
      error: err => {
        this.logger.error('Failed to load tree', err);
        // A 403 is not a load failure: it means the requested node is not in this
        // member's downline. Saying "failed to load" sent the user looking for an
        // outage instead of telling them what actually happened.
        this.error.set(this.translateService.translate(
          err?.status === 403 ? 'user_tree.error_not_authorized' : 'user_tree.error_load'
        ));
        this.loading.set(false);
      }
    });
  }

  navigateTo(userId: number | null): void {
    const lang = this.translateService.getActiveLang() || 'en';
    const path = userId === null ? ['/', lang, 'user-tree'] : ['/', lang, 'user-tree', userId];
    this.router.navigate(path, { queryParams: this.sortQueryParams() });
  }

  /** Carried into every navigation so the chosen order survives the drill-down. */
  private sortQueryParams(): Record<string, string> {
    return { sort: this.sortColumn(), dir: this.sortDirection() };
  }

  goToPage(page: number): void {
    const data = this.treeData();
    if (!data) return;

    if (page < 1 || page > data.pagination.total_pages) return;

    this.currentPage.set(page);
  }

  sortBy(column: string): void {
    const direction = this.sortColumn() === column && this.sortDirection() === 'asc' ? 'desc' : 'asc';

    // Write to the URL and let the queryParamMap subscription set the signals, so
    // sorting and arriving on a sorted link take exactly the same code path.
    // replaceUrl keeps the back button walking the tree rather than the sorts.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sort: column, dir: direction },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  getSortIcon(column: string): string {
    if (this.sortColumn() !== column) return '';
    return this.sortDirection() === 'asc' ? 'cilArrowTop' : 'cilArrowBottom';
  }

  /**
   * Only ACTIVE qualifies — every point, rank and closing commission hangs off it,
   * so it is the one state that gets a positive colour. PENDING/EXPIRED are warned
   * about rather than shown as fine, and "no membership" is not an error state.
   */
  membershipColor(user: UserTreeNode): string {
    switch (user.membership_status) {
      case 'ACTIVE':
        return 'success';
      case 'PENDING_APPLICATION':
      case 'PENDING_CANCELLATION_PERIOD':
        return 'warning';
      case 'EXPIRED':
        return 'danger';
      case 'CANCELED':
      case 'DECLINED':
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  membershipLabel(user: UserTreeNode): string {
    const status = user.membership_status;
    if (!status) return this.translateService.translate('user_tree.membership_none');

    return this.translateService.translate(`user_tree.membership_${status.toLowerCase()}`);
  }
}
